/**
 * Frontend mirror of the backend Lambda `validateCreativePackages` pricing.
 *
 * The server prices creative packages by type — this MUST match cent-for-cent so
 * the displayed creative subtotal equals the charge:
 *   - isHourly = (packageType === 'BASIC')  → pricePerHour × billableHours
 *   - any other packageType                 → flat `price`, billed ONCE (no hours)
 *
 * `selectedPackages` are the objects returned by getSelectedPackages(), which
 * spread the GraphQL package and therefore carry `packageType`, `price` and
 * `pricePerHour`.
 *
 * Defensive divergence from the Lambda: where the server THROWS on an invalid
 * price (e.g. a BASIC package with no pricePerHour), this helper clamps the line
 * to 0 and still renders — the server-amount gate catches genuinely broken configs.
 */

const roundMoney = (value) => Math.round((Number(value) || 0) * 100) / 100;

export function calculateCreativePackagesPrice(selectedPackages, billableHours) {
  const hours = Number(billableHours) || 0;

  if (!Array.isArray(selectedPackages) || selectedPackages.length === 0) {
    return { total: 0, lines: [] };
  }

  const lines = selectedPackages.map((pkg) => {
    const isHourly = pkg?.packageType === 'BASIC';

    let unitPrice;
    let lineHours;
    let lineTotal;

    if (isHourly) {
      unitPrice = Number(pkg?.pricePerHour) || 0;
      lineHours = hours;
      lineTotal = roundMoney(unitPrice * hours);
    } else {
      unitPrice = Number(pkg?.price) || 0;
      lineHours = null;
      lineTotal = roundMoney(unitPrice);
    }

    return {
      id: pkg?.id,
      name: pkg?.name || 'Creative-Paket',
      isHourly,
      unitPrice,
      hours: lineHours,
      lineTotal,
    };
  });

  const total = roundMoney(lines.reduce((sum, line) => sum + line.lineTotal, 0));

  return { total, lines };
}
